import { ReactElement } from 'react';

import {
   DayOfWeek,
   TimeOfDay,
   WaitlistEntryQuery,
   WaitlistEntryUpdateCommand,
} from '@dreryk/egabinet-waitlist-frontend-api';
import { PatientId } from '@dreryk/egabinet-patient-published-language-api';
import { PractitionerId } from '@dreryk/egabinet-personnel-published-language-api';

import {
   DataSyncWaitingEventInterceptor,
   dispatchSnackbarPositiveEvent,
   ModalConfirmButton,
} from '@dreryk/react-components';
import { refetchCqrsQuery, useCqrsCommand, useDataSyncing } from '@dreryk/command-query-react-common';
import { Choice } from '@dreryk/watermelon';
import { FormContextValues } from '@dreryk/react-common';

import { translationKeys } from '../translations';
import { isPatientSimple } from '../form/fields/PatientField/PatientField.utils';
import { isPractitionerView } from '../form/fields/PractitionerField/PractitionerView.utils';

import { WaitlistEntryUpdateFormBean } from './WaitlistEntryUpdate.types';

type PatientAvailability = { [D in DayOfWeek]?: TimeOfDay[] };

type WaitlistEntryUpdateModalConfirmButtonProps = {
   handleSubmit: FormContextValues<WaitlistEntryUpdateFormBean>['handleSubmit'];
   onSuccess: VoidFunction;
   entryId: number;
};

export const WaitlistEntryUpdateModalConfirmButton = (
   props: WaitlistEntryUpdateModalConfirmButtonProps
): ReactElement => {
   const { executeAsync: updateWaitlistEntry } = useCqrsCommand<void, WaitlistEntryUpdateFormBean>(
      (parameters) => {
         const dayTimePreferences = Object.keys(parameters.dayTimePreferences).reduce<PatientAvailability>(
            (acc, dayOfWeek) => {
               const currentPreference = parameters.dayTimePreferences[dayOfWeek as DayOfWeek];
               if (currentPreference.length) {
                  acc[dayOfWeek as DayOfWeek] = currentPreference;
               }
               return acc;
            },
            {}
         );

         const command = new WaitlistEntryUpdateCommand();
         command.id = props.entryId;
         command.patientId = isPatientSimple(parameters.patient)
            ? PatientId.of(parameters.patient.id)
            : parameters.patient.id;
         command.practitionerId =
            parameters.practitioner && isPractitionerView(parameters.practitioner)
               ? PractitionerId.of(parameters.practitioner.id)
               : parameters.practitioner!.id;
         command.importance = parameters.urgent ? 'IMPORTANT' : 'REGULAR';
         command.note = Choice.withUseDefaultIfNull(parameters.note);
         command.reservationPreference = parameters.reservationPreference;
         command.locationId = Choice.withUseDefaultIfNull(parameters.location?.id ?? null);
         command.encounterMode = parameters.videoConsultation ? 'VIDEO_CONSULTATION' : 'ON_SITE';
         command.receptionServiceId = Choice.withUseDefaultIfNull(parameters.service ? parameters.service.id : null);
         command.patientAvailability = Choice.withUseDefaultIfNull(
            Object.keys(dayTimePreferences).length ? dayTimePreferences : null
         );
         return command;
      },
      {
         onSuccess: () => {
            props.onSuccess();
            dispatchSnackbarPositiveEvent(translationKeys.changesSaved);
            refetchCqrsQuery(WaitlistEntryQuery);
         },
      }
   );

   return (
      <DataSyncWaitingEventInterceptor waitUntil={useDataSyncing()}>
         {(waiting) => (
            <ModalConfirmButton
               label={translationKeys.save}
               onClick={props.handleSubmit(updateWaitlistEntry)}
               disabled={waiting}
            />
         )}
      </DataSyncWaitingEventInterceptor>
   );
};
